import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    if (token) {
      loadUser();
      loadCartCount();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      if (error.response?.status === 403) {
        alert(error.response.data.detail);
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loadCartCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartCount(response.data.item_count || 0);
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    // Check if MFA is required
    if (response.data.mfa_required) {
      return {
        mfa_required: true,
        mfa_session_token: response.data.mfa_session_token,
        message: response.data.message
      };
    }
    
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return { mfa_required: false, user: userData };
  };

  const verifyMFA = async (code, sessionToken) => {
    const response = await axios.post(`${API}/auth/mfa/verify`, {
      code,
      session_token: sessionToken
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const resendMFACode = async (sessionToken) => {
    const response = await axios.post(`${API}/auth/mfa/resend`, {
      session_token: sessionToken
    });
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCartCount(0);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const isAdmin = () => {
    return ['super_admin', 'user_admin', 'content_admin', 'practitioner_admin'].includes(user?.role);
  };

  const hasRole = (roles) => {
    if (!Array.isArray(roles)) roles = [roles];
    return roles.includes(user?.role);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      verifyMFA,
      resendMFACode,
      register, 
      logout, 
      loading, 
      updateUser,
      isAdmin,
      hasRole,
      cartCount,
      refreshCart: loadCartCount
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { API, BACKEND_URL };
